import React, { useEffect } from 'react';

import {
  Button,
  Icon,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Text,
} from '@eluve/components';
import { useAppointmentId } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import {
  useAppointmentFilesStore,
  useAppointmentFilesStoreApi,
  useUserFileSystemStore,
} from '@eluve/user-local-files';

export interface AppointmentFilesDrawerProps {}

export const AppointmentFilesDrawer: React.FC<
  AppointmentFilesDrawerProps
> = () => {
  const appointmentId = useAppointmentId();
  const logger = useNamedLogger('AppointmentFilesDrawer');
  const storeApi = useAppointmentFilesStoreApi();
  const localFiles = useAppointmentFilesStore((state) => state.files);

  const readFilesFromOpfs = useAppointmentFilesStore(
    (store) => store.readFilesFromOpfs,
  );
  const downloadFileFromOpfs = useAppointmentFilesStore(
    (store) => store.downloadFileFromOpfs,
  );
  const userFileSystem = useUserFileSystemStore(
    (store) => store.userFileSystem,
  );

  // TODO(jesse)[ELU-963]: We can probably make this more reactive in the future
  // but due to the nature of the FileSystemAPI polling for changes to sync
  // into react land is suitable for the short term
  useEffect(
    function syncFilesFromOpfsToStore() {
      let isScanning = false;
      const syncFilesToStore = async () => {
        if (isScanning) {
          return;
        }

        const result = await readFilesFromOpfs();
        if (result.type === 'directoryExists') {
          const { files } = result;
          storeApi.setState({ files });
        }
        isScanning = false;
      };

      syncFilesToStore();

      const interval = setInterval(syncFilesToStore, 5000);

      return () => {
        clearInterval(interval);
      };
    },
    [readFilesFromOpfs, storeApi, logger, userFileSystem, appointmentId],
  );

  if (userFileSystem.type === 'unavailable') {
    return null;
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Icon name="Files" size="auto" />
          Files
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom">
        <div className="container">
          <SheetHeader>
            <SheetTitle>Appointment Files</SheetTitle>
            <SheetDescription>
              {localFiles?.length
                ? 'Files will be removed from your device once the appointment is complete'
                : 'There are no local files on this device.'}
            </SheetDescription>
          </SheetHeader>
          <div className="mx-6 grid gap-1">
            {localFiles.map((f) => (
              <Stack
                key={f.name}
                className="max-w-xl border p-2"
                direction="horizontal"
                align="center"
                justify="between"
                fullWidth={false}
              >
                <Stack
                  direction="horizontal"
                  align="center"
                  gap={2}
                  fullWidth={false}
                >
                  <Icon
                    name="FileAudio"
                    size="xl"
                    className="text-text-primary"
                  />
                  <Text>{f.name}</Text>
                </Stack>
                <Stack
                  direction="horizontal"
                  align="center"
                  gap={4}
                  fullWidth={false}
                >
                  <Text weight="bold">{f.size}</Text>
                  <Button
                    size="sm"
                    onClick={() => downloadFileFromOpfs(f.name)}
                  >
                    Download
                  </Button>
                </Stack>
              </Stack>
            ))}
          </div>
          <SheetFooter></SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
